import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  BOTTOM_SHEET_COLLAPSED_HEIGHT,
  BOTTOM_SHEET_DRAG_THRESHOLD,
  BOTTOM_SHEET_TAP_DISTANCE,
} from '../../utility/quota';
import { BottomSheetDrawer } from '../common/drawerPopover/styled';

// Drag zone over the header's grabber (BottomSheetDrawer draws it in the header's top padding), kept apart from the
// title so buttons in the title don't move the sheet.
const Handle = styled.div`
  position: absolute;
  inset: 0 0 auto;
  height: 18px;
  touch-action: none;
  cursor: grab;
  user-select: none;
`;

const Title = styled.div`
  padding-block: 6px 4px;
`;

// Mobile bottom sheet that opens at about three quarters of the screen. Dragging the handle up (or tapping it)
// takes it full screen; dragging down shrinks it back, or closes it when it's already collapsed. The body scrolls
// inside the sheet and the footer stays pinned. Opened through the ref like the side Drawer (openDrawer /
// closeDrawer), so a desktop drawer can swap to it on mobile.
const ExpandableBottomSheet = forwardRef(({ onCloseDrawer, title, footer, children }, outerRef) => {
  const { t } = useTranslation();
  const ref = useRef();
  const startY = useRef(null);
  const [expanded, setExpanded] = useState(false);

  useImperativeHandle(outerRef, () => ({
    openDrawer() {
      setExpanded(false);
      ref.current?.openDrawer();
    },
    closeDrawer() {
      ref.current?.closeDrawer();
    },
    isOpen() {
      return ref.current?.isOpen?.();
    },
  }));

  const onPointerDown = (e) => {
    startY.current = e.clientY;
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerUp = (e) => {
    if (startY.current == null) return;
    const dy = e.clientY - startY.current;
    startY.current = null;
    if (Math.abs(dy) < BOTTOM_SHEET_TAP_DISTANCE) {
      setExpanded((value) => !value);
    } else if (dy < -BOTTOM_SHEET_DRAG_THRESHOLD) {
      setExpanded(true);
    } else if (dy > BOTTOM_SHEET_DRAG_THRESHOLD) {
      if (expanded) setExpanded(false);
      else ref.current?.closeDrawer();
    }
  };

  return (
    <BottomSheetDrawer
      ref={ref}
      rootClassName="bottom-sheet"
      className="drawer-container"
      placement="bottom"
      height={expanded ? '100%' : BOTTOM_SHEET_COLLAPSED_HEIGHT}
      onCloseDrawer={onCloseDrawer}
      footer={footer}
      bodyStyle={{ padding: 16 }}
      title={
        <>
          <Handle
            role="button"
            tabIndex={0}
            aria-label={expanded ? t('Collapse') : t('Expand')}
            aria-expanded={expanded}
            onPointerDown={onPointerDown}
            onPointerUp={onPointerUp}
            onPointerCancel={() => {
              startY.current = null;
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setExpanded((value) => !value);
              }
            }}
          />
          <Title>{title}</Title>
        </>
      }
    >
      {children}
    </BottomSheetDrawer>
  );
});

ExpandableBottomSheet.displayName = 'ExpandableBottomSheet';

export default ExpandableBottomSheet;
